import { Head } from 'vite-react-ssg'

const BASE_URL  = 'https://myschoolportal.xyz'
const SITE_NAME = 'MySchoolPortal'
const OG_IMAGE  = `${BASE_URL}/og-image.svg`

const ORG_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Tvantra',
  url: BASE_URL,
  logo: `${BASE_URL}/favicon.svg`,
  sameAs: [],
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'info@myschoolportal.xyz',
    contactType: 'customer support',
    areaServed: 'GH',
    availableLanguage: 'English',
  },
}

// Signals the site's main sections to search engines as candidates for
// sitelinks. Doesn't guarantee sitelinks — that's algorithmic — but helps
// eligibility by making the structure explicit rather than crawled/inferred.
const NAV_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  itemListElement: [
    { '@type': 'SiteNavigationElement', position: 1, name: 'Features', url: `${BASE_URL}/features` },
    { '@type': 'SiteNavigationElement', position: 2, name: 'Products', url: `${BASE_URL}/products` },
    { '@type': 'SiteNavigationElement', position: 3, name: 'Blog',     url: `${BASE_URL}/blog`     },
    { '@type': 'SiteNavigationElement', position: 4, name: 'About',    url: `${BASE_URL}/about`    },
    { '@type': 'SiteNavigationElement', position: 5, name: 'Contact',  url: `${BASE_URL}/contact`  },
  ],
}

export default function SEO({
  title,
  description,
  path = '',
  image = OG_IMAGE,
  schema = null,
  noindex = false,
}) {
  const fullTitle = title
    ? (title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`)
    : `${SITE_NAME} | School Management System for Ghana`
  const canonical  = `${BASE_URL}${path}`

  return (
    <Head>
      {/* charset MUST be the first thing in <head> (within the first 1024 bytes)
          or browsers may re-parse. Helmet injects its managed tags at the very
          top of <head> during SSG, pushing index.html's own <meta charset> past
          the limit — so we emit it here, before the title, to keep it first. */}
      <meta charSet="utf-8" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type"        content="website" />
      <meta property="og:site_name"   content={SITE_NAME} />
      <meta property="og:url"         content={canonical} />
      <meta property="og:title"       content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image"       content={image} />
      <meta property="og:locale"      content="en_GH" />

      {/* Twitter */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:title"       content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={image} />

      {/* Organisation + site navigation schema on every page */}
      <script type="application/ld+json">
        {JSON.stringify(ORG_SCHEMA)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(NAV_SCHEMA)}
      </script>

      {/* Page-specific schema (single object or array) */}
      {schema && (Array.isArray(schema) ? schema : [schema]).map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Head>
  )
}
